import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button, Input } from "../ui";
import { useSession } from "./session";
import { SettingsSection } from "./SettingsSection";
import { SecretInput, SiteGroup } from "./SiteFields";
import { clearSiteValue, putSiteValue, useSiteConf, validSecretName, type SiteSnapshot } from "./siteconf";

const sub = "text-[12px] font-semibold text-neutral-600 dark:text-neutral-300";

const group = (snap: SiteSnapshot | null, id: string) => snap?.groups.find((g) => g.id === id)?.fields ?? [];

function useAdminConf() {
  const isAdmin = useSession((s) => !!s.session?.profile?.isAdmin);
  return { isAdmin, ...useSiteConf(isAdmin) };
}

/** Settings → Assistant: which models answer (ADR 0011). Admin only; the rest of the section is per person. */
export function AssistantModelSettings() {
  const { t } = useTranslation();
  const { isAdmin, snap, refresh } = useAdminConf();
  if (!isAdmin || !snap) return null;
  const cloud = group(snap, "assistantCloud");
  const cloudProvider = cloud.find((f) => f.name === "DRANG_LLM_PROVIDER_2")?.value ?? "";
  const cloudOn = cloudProvider !== "" && cloudProvider !== "none";
  return (
    <SettingsSection
      id="assistant-models"
      intro={t("siteconf.assistantHint")}
      summary={t("settings.summary.assistantModels")}
      title={t("siteconf.modelsTitle")}
      testId="assistant-models"
      folded
    >
      <div className="flex flex-col gap-2">
        <h3 className={sub}>{t("siteconf.group.assistant")}</h3>
        <SiteGroup
          fields={group(snap, "assistant")}
          onChanged={() => void refresh()}
          testId="site-group-assistant"
        />
      </div>
      <div className="flex flex-col gap-2">
        <h3 className={sub}>{t("siteconf.group.assistantCloud")}</h3>
        <SiteGroup
          fields={cloudOn ? cloud : cloud.filter((f) => f.name === "DRANG_LLM_PROVIDER_2")}
          onChanged={() => void refresh()}
          testId="site-group-assistantCloud"
        />
      </div>
    </SettingsSection>
  );
}

/** Settings → Backup and email: off-site copies of the site backup and the account automations send mail with. */
export function BackupEmailSettings() {
  const { t } = useTranslation();
  const { isAdmin, snap, refresh } = useAdminConf();
  if (!isAdmin || !snap) return null;
  return (
    <SettingsSection
      id="settings-backup-email"
      intro={t("settings.intro.backupEmail")}
      title={`${t("siteconf.group.backup")} · ${t("siteconf.group.email")}`}
      testId="settings-backup-email"
      folded
    >
      <h3 className={sub}>{t("siteconf.group.backup")}</h3>
      <p className="-mt-2 text-[12.5px] text-neutral-500">{t("siteconf.backupHint")}</p>
      <SiteGroup fields={group(snap, "backup")} onChanged={() => void refresh()} testId="site-group-backup" />
      <h3 className={`${sub} mt-2`}>{t("siteconf.group.email")}</h3>
      <p className="-mt-2 text-[12.5px] text-neutral-500">{t("siteconf.emailHint")}</p>
      <SiteGroup fields={group(snap, "email")} onChanged={() => void refresh()} testId="site-group-email" />
    </SettingsSection>
  );
}

/** Settings → Secrets: tokens of integrations, secrets of automations, private calendar addresses. */
export function SecretsSettings() {
  const { t } = useTranslation();
  const { isAdmin, snap, refresh } = useAdminConf();
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!isAdmin || !snap) return null;
  const add = async () => {
    const n = name.trim();
    if (!validSecretName(n)) return setError(t("siteconf.badName"));
    try {
      await putSiteValue(n, value);
      setName("");
      setValue("");
      setError(null);
      void refresh();
    } catch (e) {
      setError(t(`errors.${(e as Error).message}`, { defaultValue: t("error.generic") }));
    }
  };
  return (
    <SettingsSection
      id="settings-secrets"
      intro={t("siteconf.secretsHint")}
      summary={t("settings.summary.secrets")}
      title={t("siteconf.secretsTitle")}
      testId="settings-secrets"
      folded
    >
      {snap.secrets.length === 0 && <p className="text-[13px] text-neutral-500">{t("siteconf.noSecrets")}</p>}
      <ul className="flex flex-col gap-3">
        {snap.secrets.map((s) => (
          <li
            key={`${s.name}|${s.source}`}
            className="flex flex-col gap-1"
            data-testid="secret-row"
            data-name={s.name}
          >
            <span className="flex items-center gap-2">
              <span className="font-mono text-[12.5px]">{s.name}</span>
              {s.hasValue && (
                <button
                  type="button"
                  aria-label={t("siteconf.deleteSecret", { name: s.name })}
                  title={t("siteconf.deleteSecret", { name: s.name })}
                  onClick={() => void clearSiteValue(s.name).then(() => refresh())}
                  className="grid h-6 w-6 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                  data-testid="secret-delete"
                >
                  <Trash2 size={13} aria-hidden />
                </button>
              )}
            </span>
            <SecretInput entry={s} label={s.name} onChanged={() => void refresh()} />
          </li>
        ))}
      </ul>
      <form
        className="flex flex-wrap items-center gap-1.5 border-t border-neutral-200 pt-3 dark:border-neutral-800"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <Input
          compact
          className="w-60 font-mono"
          spellCheck={false}
          placeholder="DRANG_AUTOMATION_…"
          aria-label={t("siteconf.newName")}
          value={name}
          onChange={(e) => setName(e.target.value.toUpperCase())}
          data-testid="secret-new-name"
        />
        <Input
          compact
          type="password"
          autoComplete="new-password"
          className="min-w-0 flex-1"
          aria-label={t("siteconf.newValue")}
          placeholder={t("siteconf.enterSecret")}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid="secret-new-value"
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={!name || !value}
          data-testid="secret-add"
        >
          {t("siteconf.add")}
        </Button>
      </form>
      {error && (
        <p role="alert" className="text-[12.5px] text-err">
          {error}
        </p>
      )}
    </SettingsSection>
  );
}
